/**
 * Remembers the builds a browser is watching, so a page refresh re-attaches
 * instead of losing them.
 *
 * A headless build is server-side and survives anything the browser does — but
 * the only pointer to it lived in component state. Refreshing the new-deck
 * wizard mid-build dropped you back on the intake form while the agent kept
 * writing slides invisibly, with no route back except the ⚙ Builds panel or
 * waiting for the deck to appear on the dashboard. The server already replays a
 * job's full event history on re-attach, so remembering the id is all it takes.
 *
 * A scope holds a LIST, not one record. It used to hold exactly one, which is
 * what made a second deck build silently orphan the first — you could start it,
 * but nothing could ever find it again. Builds are backgroundable now (start
 * one, walk away, start another), so every live build a tab knows about has to
 * survive here, not just the newest.
 *
 * Deliberately localStorage, not sessionStorage: a crashed or closed tab should
 * still be able to pick its builds back up.
 */

const PREFIX = 'studio.activeBuild.';

/** Past this, a remembered build is certainly finished (60 min hard cap, then
 *  reaped 30 min after it goes terminal) and re-attaching would only 404. */
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

/** Enough for every build slot several times over. Guards against a scope
 *  growing without bound in a tab left open all day. */
const MAX_RECORDS = 10;

const keyFor = (scope: string) => PREFIX + scope;

/** The pointer back to a running build. `slug` and `title` are null until the
 *  server has named the deck (a new-deck build is under way before it has a
 *  slug). */
export type RememberedBuild = { jobId: string; slug: string | null; title: string | null };

/** The stored shape: a RememberedBuild plus its remember-time. */
type StoredBuild = { jobId: string; slug: string | null; title: string | null; at: number };

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object';
}

/** Parse a scope's stored value into a record list, newest first.
 *
 *  Narrowed rather than asserted: this is a string store shared with every
 *  past version of the app, so each record's shape is a hope, not a fact.
 *  Tolerates the pre-list format (a bare `{jobId, slug, at}` object) so an
 *  open tab mid-build across the deploy keeps its build rather than losing it. */
function read(scope: string): StoredBuild[] {
  try {
    const raw = localStorage.getItem(keyFor(scope));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    return list.filter((r): r is StoredBuild => isRecord(r) && typeof r.jobId === 'string' && !!r.jobId);
  } catch {
    return [];
  }
}

function write(scope: string, list: StoredBuild[]): void {
  try {
    if (!list.length) localStorage.removeItem(keyFor(scope));
    else localStorage.setItem(keyFor(scope), JSON.stringify(list.slice(0, MAX_RECORDS)));
  } catch {
    /* private mode / quota — remembering is an optimisation, never required */
  }
}

/** Note a build this scope is watching. `scope` is 'new-deck' or
 *  `section:<slug>`. Re-remembering a jobId updates it in place rather than
 *  duplicating it (a re-attach after a reconnect must not grow the list). */
export function rememberBuild(
  scope: string,
  { jobId, slug, title }: { jobId: string; slug?: string | null; title?: string | null },
): void {
  if (!jobId) return;
  const rest = read(scope).filter((r) => r.jobId !== jobId);
  write(scope, [{ jobId, slug: slug || null, title: title || null, at: Date.now() }, ...rest]);
}

/** Every live build this scope was watching, newest first. Stale records are
 *  dropped from storage, not merely filtered out of the result. */
export function recallBuilds(scope: string): RememberedBuild[] {
  const list = read(scope);
  const fresh = list.filter((r) => typeof r.at === 'number' && Date.now() - r.at <= MAX_AGE_MS);
  if (fresh.length !== list.length) write(scope, fresh);
  return fresh.map((r) => ({ jobId: r.jobId, slug: r.slug || null, title: r.title || null }));
}

/** The newest live build in this scope, or null. For the single-build callers
 *  (section builds are genuinely one-per-deck). */
export function recallBuild(scope: string): { jobId: string; slug: string | null } | null {
  const [newest] = recallBuilds(scope);
  return newest ? { jobId: newest.jobId, slug: newest.slug } : null;
}

/** Forget one build, or the whole scope when `jobId` is omitted.
 *
 *  Pass the jobId whenever one build of several has ended — clearing the scope
 *  wholesale is what would drop the builds still running beside it. */
export function forgetBuild(scope: string, jobId?: string): void {
  if (!jobId) {
    try {
      localStorage.removeItem(keyFor(scope));
    } catch {
      /* nothing to do */
    }
    return;
  }
  write(
    scope,
    read(scope).filter((r) => r.jobId !== jobId),
  );
}
